import { Plus } from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Button } from "@/core/components/ui/button";

/**
 * The PackChat state that crosses the header/content boundary.
 *
 * Deliberately only that: the tab's New thread button lives in the page header,
 * while the pane it takes over lives in a card several hundred lines further
 * down the same page. Everything else PackChat needs — the search, which
 * technician is selected, which filter is on, which threads are unfolded — never
 * leaves the panel and stays in local state there.
 */
interface PackChatSection {
  /** Whether the manager-start form has taken over the reading pane. */
  starting: boolean;
  startThread: () => void;
  stopStarting: () => void;
}

const PackChatSectionContext = createContext<PackChatSection | null>(null);

/**
 * Holds the PackChat state the page header and the PackChat panel share.
 *
 * This exists so neither side has to reach for a portal. The controls belong in
 * the page header — the same slot Packs uses — but the header is rendered as a
 * sibling far above the section, so a section that owns its own state cannot put
 * anything up there without either lifting that state into the page or portaling
 * a node out of its own tree. Portaling put the React tree out of step with the
 * DOM: events bubbled to the wrong parent, the target could be null (it was, for
 * one revision, and the controls silently rendered back inside the card), and
 * tests needed a live DOM node to render at all.
 *
 * A provider whose scope covers both readers fixes it with ordinary React. Mount
 * it unconditionally, above the header: swapping it in and out per active section
 * would change the element type at the top of the page and remount everything
 * under it, including the Grid, which is kept mounted on purpose so a workbook
 * and its selection survive tab switches.
 */
export const PackChatSectionProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [starting, setStarting] = useState(false);

  const startThread = useCallback(() => setStarting(true), []);
  const stopStarting = useCallback(() => setStarting(false), []);

  const value = useMemo(
    () => ({ starting, startThread, stopStarting }),
    [starting, startThread, stopStarting],
  );

  return (
    <PackChatSectionContext.Provider value={value}>
      {children}
    </PackChatSectionContext.Provider>
  );
};

export const usePackChatSection = () => {
  const section = useContext(PackChatSectionContext);
  if (!section) {
    throw new Error(
      "PackChat components must be rendered inside a PackChatSectionProvider.",
    );
  }
  return section;
};

/**
 * PackChat's control, for the page header's section slot.
 *
 * Rendered by the page beside the period picker, the way Packs renders its own —
 * so the tab that is showing owns the header's actions and the workbook's step
 * aside. Only the primary action comes up here: searching is done against the
 * list, so that control sits on the list's own filter row instead.
 */
export const PackChatHeaderActions = () => {
  const { startThread } = usePackChatSection();

  return (
    <Button size="sm" onClick={startThread}>
      <Plus aria-hidden />
      New thread
    </Button>
  );
};
